/**
 * Admin V3 — Financial / Ledger / Grand livre — top-level page
 *
 * Mounted at: /admin-v3/financial/ledger (see AdminV3.js)
 *
 * GOVERNANCE — FINANCIAL CORE READ-ONLY:
 *   - This module is the canonical visibility surface for the wallet_ledger
 *     collection, which is the source-of-truth for driver financial state.
 *   - The platform invariant is: balance is DERIVED from ledger, not stored.
 *     Any displayed balance must be presented as ledger-derived, never as
 *     stored authority.
 *   - Strictly read-only. Zero references to payout execution, withdrawal
 *     execution, retry, batch processing, Stripe transfer creation, or any
 *     mutation surface. Phase 3+ scope at minimum for any mutation.
 *
 * SCOPE FOR PHASE 2.13:
 *   - GET /admin/control-tower/financial/ledger (RBAC: ADMIN_FINANCIAL_LEDGER)
 *     Server-side filters: driver_id, entry_type, limit (default 100, ≤500).
 *   - Client-side search over loaded page (reason, booking_id).
 *   - Per-row expansion with full entry metadata.
 *
 * ENUM TAXONOMY DRIFT (defensive rendering):
 *   Production entries on disk may carry either the legacy LedgerEntryType
 *   (7 values) or the canonical OperationType (9 values) per the Layer 3.2
 *   → 3.7 backend migration. Render prefers operation_type when present,
 *   falls back to entry_type, then to raw key.
 *
 * BACKEND DRIFT NOTE:
 *   admin_control_tower_v2.py:1194 sorts by `.sort("timestamp", -1)` but the
 *   stored field is `created_at`. Result order is therefore not guaranteed
 *   to be DESC by time. This module sorts client-side by `created_at` DESC
 *   to ensure consistent operator UX. Backend sort fix is a backlog item.
 *
 * EXTERNAL PROVIDER IDENTIFIERS:
 *   The endpoint may return `stripe_transfer_id`. Phase 2.13 does not
 *   display it (consistent with the broader stripe_account_id omission
 *   policy from Phase 2.11).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { BookOpen } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import FinancialLedgerSearchBar from './FinancialLedgerSearchBar';
import FinancialLedgerTable from './FinancialLedgerTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 100;

const FinancialLedgerPage = () => {
  const locale = DEFAULT_LOCALE;

  const [entryType, setEntryType] = useState('');
  const [driverId, setDriverId] = useState('');
  const [debouncedDriverId, setDebouncedDriverId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedDriverId(driverId.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [driverId]);

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = { limit: DEFAULT_LIMIT };
  if (debouncedDriverId) queryParams.driver_id  = debouncedDriverId;
  if (entryType)         queryParams.entry_type = entryType;

  const { data, loading, error, refetch } = useAdminQuery(
    '/admin/control-tower/financial/ledger',
    queryParams
  );

  // Defensive: backend sorts by a nonexistent "timestamp" field; sort
  // client-side by created_at DESC for consistent UX.
  const sortedEntries = useMemo(() => {
    const raw = Array.isArray(data?.entries) ? [...data.entries] : [];
    raw.sort((a, b) => {
      const ta = a?.created_at || '';
      const tb = b?.created_at || '';
      return ta < tb ? 1 : ta > tb ? -1 : 0;
    });
    return raw;
  }, [data]);

  // Client-side search over loaded page (reason + booking_id)
  const entries = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return sortedEntries;
    return sortedEntries.filter((e) =>
      (e.reason || '').toLowerCase().includes(q) ||
      (e.booking_id || '').toLowerCase().includes(q)
    );
  }, [sortedEntries, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(debouncedDriverId) || Boolean(entryType);
  const isAtBackendCap = sortedEntries.length === DEFAULT_LIMIT;
  const showSkeleton = loading && !data;

  return (
    <SectionCard
      title={t('financial.ledger.title', locale)}
      icon={BookOpen}
      iconClass="bg-indigo-100 text-indigo-600"
    >
      <div className="space-y-4">
        {/* Balance-derivation footnote (visible at all times, never bold) */}
        <p
          className="text-[11px] italic text-slate-400 -mt-2"
          data-testid="adminv3-financial-ledger-subtitle"
        >
          {t('financial.ledger.subtitle', locale)}
        </p>

        <FinancialLedgerSearchBar
          entryType={entryType}
          onEntryTypeChange={setEntryType}
          driverId={driverId}
          onDriverIdChange={setDriverId}
          search={search}
          onSearchChange={setSearch}
          resultsCount={entries.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <FinancialLedgerTable
          entries={entries}
          loading={showSkeleton}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />

        {isAtBackendCap && !loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-financial-ledger-cap-hint"
          >
            {t('financial.ledger.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default FinancialLedgerPage;
